import { useContext, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { SocketContext } from '@/contexts/socket';
import { sessionIdSelector } from '@/state/session';
import { SocketEvent } from '@/ts/socket';
const checkUrlValidity = async (url) => {
    const controller = new AbortController();
    try {
        const response = await fetch(url, {
            method: 'GET',
            signal: controller.signal,
        });
        if (!response.ok) {
            return false;
        }
        // Abort the request since we only needed the headers
        controller.abort();
        return true;
    }
    catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            // This is expected since we abort after checking headers
            return true;
        }
        return false;
    }
};
export const useAttachmentUrls = (attachments) => {
    const socket = useContext(SocketContext);
    const session_id = useRecoilValue(sessionIdSelector);
    const [attachmentUrls, setAttachmentUrls] = useState({});
    useEffect(() => {
        if (!socket || !(attachments === null || attachments === void 0 ? void 0 : attachments.length))
            return;
        const handleDownloadUrlCreated = (data) => {
            if (!data.download_url) {
                return;
            }
            setAttachmentUrls((prev) => (Object.assign(Object.assign({}, prev), { [data.file_id]: data.download_url })));
        };
        socket.on(SocketEvent.session_files_download_request_success, handleDownloadUrlCreated);
        Promise.all(attachments.map(async (attachment) => {
            if (!attachment.id) {
                return;
            }
            // If attachment has a URL, validate it first
            if (attachment.url) {
                const isValid = await checkUrlValidity(attachment.url);
                if (isValid) {
                    setAttachmentUrls((prev) => (Object.assign(Object.assign({}, prev), { [attachment.id]: attachment.url })));
                    return;
                }
            }
            // If URL is invalid (e.g. sticky sessions), request a new one
            return new Promise((resolve) => {
                socket.emit(SocketEvent.session_files_download_request, {
                    file_id: attachment.id,
                    file_name: attachment.filename,
                    session_id,
                    original_name: attachment.title,
                });
                resolve();
            });
        })).catch(() => { });
        return () => {
            socket.off(SocketEvent.session_files_download_request_success, handleDownloadUrlCreated);
        };
    }, [socket, session_id, attachments]);
    return attachmentUrls;
};
